import React from 'react'
import { Grid, Typography, Paper, CircularProgress } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { printValue } from '../utils/PrintValue'

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1
  },
  paper: {
    padding: theme.spacing(2),
    color: 'rgba(0,0,0,0.9)',
    height: '100%'
  },
  descript: { fontSize: '0.8rem', color: '#545454' },
  descriptVal: { fontSize: '0.85rem' },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4)
  }
}))

const displayText = v => {
  if (v === null || v === undefined) return ''
  if (typeof v !== 'string') return v
  const trimmed = v.trim()
  if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined') return ''
  // Athena returns a bare punctuation string (e.g. ',') for some empty address fields
  if (/^[,.\s]*$/.test(trimmed)) return ''
  return v
}

const formatCityStateZip = (city, state, zip) => {
  const cityState = [displayText(city), displayText(state)].filter(Boolean).join(', ')
  return [cityState, displayText(zip)].filter(Boolean).join(' ')
}

export default function CandidateMain(props) {
  const classes = useStyles()
  const { data, isLoading } = props

  const renderValue = (lookup, description, options = {}) => {
    return printValue(data, lookup, description, null, null, {
      ...options,
      classes: {
        description: classes.descript,
        value: classes.descriptVal
      }
    })
  }

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    )
  }

  const hasData = data && Object.keys(data).length > 0

  if (!hasData) {
    return (
      <div className={classes.loadingContainer}>
        <Typography variant='subtitle1'>No data available</Typography>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <Grid
        container
        spacing={1}
        direction='row'
        justifyContent='flex-start'
        alignItems='stretch'
      >
        {/* Row 1: Main Info + Contact | Application Status */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} className={classes.paper}>
            <Grid
              container
              spacing={0}
              direction='row'
              justifyContent='space-evenly'
              alignItems='flex-start'
            >
              <Grid item xs={12} sm={6}>
                <Typography variant='h5'>Main Info</Typography>
                {renderValue('NGIVENNAME', 'First Name')}
                {renderValue('NFAMILYNAME', 'Last Name')}
                {renderValue('NMIDDLENAME', 'Middle Name')}
                {renderValue('NPREFERREDGIVENNAME', 'Preferred Name')}
                {renderValue('HRORGANIZATION', 'HR Org')}
                {renderValue('CANDIDATESTATE', 'Status')}
                {(() => {
                  const linkedEmployee = data.LINKEDEMPLOYEE
                  const isLinked = linkedEmployee && linkedEmployee !== '0' && linkedEmployee !== ''
                  if (isLinked) {
                    return (
                      <div>
                        <span style={{ fontSize: '0.8rem', color: '#545454' }}>Linked Employee:  </span>
                        <span
                          style={{ fontSize: '0.85rem', color: '#1976d2', cursor: 'pointer' }}
                          onClick={() => window.open(`/employee?company=${data.HRORGANIZATION}&employee=${linkedEmployee}`, '_blank')}
                        >
                          {linkedEmployee}
                        </span>
                      </div>
                    )
                  }
                  return renderValue('LINKEDEMPLOYEE', 'Linked Employee')
                })()}
                {renderValue('ELIGIBILITY', 'Hire Eligibility')}
                {renderValue('CANDIDATEDONOTHIREREASON', 'Do Not Hire Reason')}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant='h5'>Contact</Typography>
                {renderValue('PCIEMAILADDRESS', 'Primary Email')}
                {renderValue('PCIALTERNATEEMAIL', 'Alternate Email')}
                {renderValue('PRIMARY_PHONE', 'Primary Phone Number')}
                {renderValue('SECONDARY_PHONE', 'Alternate Phone Number')}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>Application Status</Typography>
            {renderValue('APPLICATIONDATE', 'Application Date')}
            {renderValue('SCREEN_DATE_STRING', 'Screen')}
            {renderValue('MANAGER_DATE_STRING', 'Hiring Manager Review')}
            {renderValue('INTERVIEW_DATE_STRING', 'Interview')}
            {renderValue('OFFER_DATE_STRING', 'Offer')}
          </Paper>
        </Grid>

        {/* Row 2: Candidate Personal | Address */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>Candidate Personal</Typography>
            <Grid
              container
              spacing={0}
              direction='row'
              justifyContent='space-evenly'
              alignItems='flex-start'
            >
              <Grid item xs={12} sm={6}>
                {renderValue('BIRTHCITY', 'Birth City')}
                {renderValue('BIRTHCOUNTRY', 'Birth Country')}
                {renderValue('CEEOCNATIONALITY', 'Nationality')}
                {renderValue('HOMECOUNTRY', 'Home Country')}
                {renderValue('BIRTHDATE', 'Birth Date')}
                {renderValue('AGE', 'Age', { type: 'int' })}
              </Grid>
              <Grid item xs={12} sm={6}>
                {renderValue('GENDER', 'Gender')}
                {renderValue('CEEOCETHNICITYCODE', 'Ethnicity')}
                {renderValue('CEEOCMARITALSTATUS', 'Marital Status')}
                {renderValue('CEEOCVETERANCODE', 'Veteran Type')}
                {renderValue('CEEOCRELIGION', 'Religion')}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={3} className={classes.paper}>
            <Typography variant='h5'>Address</Typography>
            <div className={classes.descriptVal}>{displayText(data['PCIPADAADDRESSLINE1'])}</div>
            {displayText(data['PCIPADAADDRESSLINE2']) && (
              <div className={classes.descriptVal}>{displayText(data['PCIPADAADDRESSLINE2'])}</div>
            )}
            {displayText(data['PCIPADAADDRESSLINE3']) && (
              <div className={classes.descriptVal}>{displayText(data['PCIPADAADDRESSLINE3'])}</div>
            )}
            {displayText(data['PCIPADAADDRESSLINE4']) && (
              <div className={classes.descriptVal}>{displayText(data['PCIPADAADDRESSLINE4'])}</div>
            )}
            <div className={classes.descriptVal}>
              {formatCityStateZip(
                data['PCIPAMUNICIPALITY'],
                data['PCIPASTATEPROVINCE'],
                data['PCIPAPOSTALCODE']
              )}
            </div>
            <br />
            <Typography variant='h5'>Supplemental Address</Typography>
            <div className={classes.descriptVal}>{displayText(data['SCIPADAADDRESSLINE1'])}</div>
            {displayText(data['SCIPADAADDRESSLINE2']) && (
              <div className={classes.descriptVal}>{displayText(data['SCIPADAADDRESSLINE2'])}</div>
            )}
            {displayText(data['SCIPADAADDRESSLINE3']) && (
              <div className={classes.descriptVal}>{displayText(data['SCIPADAADDRESSLINE3'])}</div>
            )}
            {displayText(data['SCIPADAADDRESSLINE4']) && (
              <div className={classes.descriptVal}>{displayText(data['SCIPADAADDRESSLINE4'])}</div>
            )}
            <div className={classes.descriptVal}>
              {formatCityStateZip(
                data['SCIPAMUNICIPALITY'],
                data['SCIPASTATEPROVINCE'],
                data['SCIPAPOSTALCODE']
              )}
            </div>
          </Paper>
        </Grid>
      </Grid>
    </div>
  )
}
